import * as React from "react";
import { Button, Card, Checkbox } from "@jhool-io/fe-components";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    IClaimAdjustments,
    IInsurancePayments,
} from "../../types/remits.types";
import PlusIcon from "../../../../components/Icons/Plus";
import AddRemitInsurancePaymentForm from "../AddRemitInsurancePaymentForm/AddRemitInsurancePaymentForm";
import InsurancePaymentItem from "./InsurancePaymentItem/InsurancePaymentItem";
import { usePostPayments } from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";

interface InsurancePaymentsProps {
    insuransePayments: IInsurancePayments[];
    invoiceId: string;
    remitClaimAdjustments: IClaimAdjustments[];
    remittanceId: string;
}

export default function InsurancePayments({
    insuransePayments,
    invoiceId,
    remitClaimAdjustments,
    remittanceId,
}: InsurancePaymentsProps) {
    const [isAddingRow, setIsAddingRow] = React.useState(false);

    const [searchParams, setSearchParams] = useSearchParams();

    const paymentsArray = searchParams.getAll("selectedPayments");

    const postPayments = usePostPayments();

    const queryClient = useQueryClient();

    const params = useParams();

    const { toast } = useToast();

    const { noteId } = params;

    const handlePostPayment = () => {
        postPayments.mutate(
            { payment_ids: paymentsArray },
            {
                onSuccess: (res) => {
                    queryClient.invalidateQueries([
                        "remit-claims",
                        {
                            note_id: noteId,
                        },
                    ]);
                    toast({
                        mode: "success",
                        message:
                            res.message || "Payment(s) posted successfully.",
                    });
                    searchParams.delete("selectedPayments");
                    setSearchParams(searchParams);
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not post payment(s).",
                    });
                },
            }
        );
    };

    const eligiblePayments = insuransePayments.filter(
        (payment) =>
            payment.payment_status !== "completed" ||
            (payment.payment_type !== "wbi" &&
                payment.payment_status !== "completed")
    );

    const handleBulkCheck = (checked: boolean) => {
        searchParams.delete("selectedPayments");

        if (checked) {
            insuransePayments.forEach((payment) => {
                searchParams.append("selectedPayments", payment.payment_id);
            });
        }

        setSearchParams(searchParams);
    };

    return (
        <Card classNames="p-24 mt-32 ">
            <div className="flex justify-between items-center mb-24">
                <h3 className="text-sm font-bold">Insurance payments</h3>
            </div>

            {paymentsArray.length > 0 ? (
                <div className=" flex items-center gap-x-4">
                    <p className=" text-sm font-medium">
                        {paymentsArray.length} selected:
                    </p>
                    <Button onClick={() => handlePostPayment()}>
                        Confirm payments
                    </Button>
                </div>
            ) : null}
            <div className="mt-24 rounded-r8 border border-strokedark w-full !overflow-x-auto">
                <div className="table w-full">
                    <div className="p-12 border-b border-b-strokedark grid gap-x-12 grid-cols-[100px_150px_300px_100px_120px_260px_150px_100px_100px_200px] justify-stretch text-left text-xs uppercase font-semibold text-gray items-center">
                        {eligiblePayments.length > 0 ? (
                            <Checkbox
                                name=""
                                value=""
                                handleChange={(e) =>
                                    handleBulkCheck(e.target.checked)
                                }
                                isChecked={
                                    eligiblePayments.length > 0 &&
                                    eligiblePayments.every((p) =>
                                        paymentsArray.includes(p.payment_id)
                                    )
                                }
                            />
                        ) : (
                            <span className="bg-blue-300 w-[100px]" />
                        )}

                        <span>payment date</span>
                        <span>payment type</span>
                        <span>amount</span>
                        <span>payment status</span>
                        <span>remark codes</span>
                        <span>eft</span>
                        <span>date posted</span>
                        <span>posted by</span>
                        <span>payment notes</span>
                    </div>
                    {insuransePayments.map((item) => (
                        <InsurancePaymentItem
                            key={item.payment_id}
                            payment={item}
                            invoiceId={invoiceId}
                            remitClaimAdjustments={remitClaimAdjustments}
                            remittanceId={remittanceId}
                        />
                    ))}
                </div>

                <div className="p-12">
                    {!isAddingRow ? (
                        <div className="flex justify-center">
                            <Button
                                size="auto"
                                variant="normal"
                                className="font-normal text-sm flex gap-x-12 items-center"
                                onClick={() => setIsAddingRow(true)}
                            >
                                <PlusIcon stroke="#0B132B" />
                                Add insurance payment
                            </Button>
                        </div>
                    ) : (
                        <AddRemitInsurancePaymentForm
                            invoiceId={invoiceId}
                            onCancelClick={() => setIsAddingRow(false)}
                            onFormSubmit={() => setIsAddingRow(false)}
                        />
                    )}
                </div>
            </div>
        </Card>
    );
}
